import { useContext } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  createUserWithEmailAndPassword,
  getIdToken,
  User
} from 'firebase/auth';
import { auth, analytics } from '../config/firebase';
import { AuthContext } from './AuthContext';
import { logEvent } from 'firebase/analytics';
import api from '../utils/api';
import { faro } from '../config/faro';
import { trackAmplitudeEvent, resetAmplitudeUser } from '../config/amplitude';
import { OFFLINE_ENABLED } from '../offline/entities';
import { clearReplica } from '../offline/db';
import { unsyncedOpCount } from '../offline/opQueue';
import {
  markIntentionalSignOut,
  clearIntentionalSignOut,
  clearClaimsCache,
} from './offlineAuthGrace';
import { resetMePermissions } from './mePermissions';
import { setSignOutNotice, SignOutNoticeCode } from './signOutNotice';
import { detectBrowserTimezone } from '../utils/timezones';

export const useAuth = () => {
  const { user, loading, graceMode } = useContext(AuthContext);

  const login = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      logEvent(analytics, 'login', {
        method: 'Email and Password',
        userId: result.user.uid
      });
      faro?.api.pushEvent('login', { method: 'email' });
      trackAmplitudeEvent('login', { method: 'email' });
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  /**
   * `profile` is captured at step 1 on purpose. The setup wizard (step 2) is
   * only reachable if the user comes back signed in after verifying their
   * email — verifying on another device lands them on /login instead — so
   * terms and identity have to be persisted here or they are simply lost.
   */
  const signup = async (
    email: string,
    password: string,
    profile?: {
      clinicName?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      termsAccepted?: boolean;
      termsVersion?: string;
    },
  ) => {
    try {
      // 1. Create user with Firebase
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // 2. Create user in backend database
      try {
        // The new clinic's Clinic.timezone. It decides reminder send times,
        // statement cut-offs and hospital service-date bucketing — sending the
        // browser's zone here is the only way a non-Eastern clinic starts out
        // correct without first discovering the setting. Optional and
        // IANA-validated server-side; a junk value is dropped, never a 400.
        const timezone = detectBrowserTimezone();
        const response = await api.post('/public/signup', {
          uid: result.user.uid,
          email: result.user.email,
          name: result.user.displayName || email.split('@')[0],
          ...(timezone ? { timezone } : {}),
          ...(profile ?? {}),
        });
      } catch (dbError) {
        console.error('Error creating user in database:', dbError);
        // Don't throw here - user is already created in Firebase
        // You might want to show a warning to the user
      }
      
      logEvent(analytics, 'sign_up', {
        method: 'Email and Password',
        userId: result.user.uid
      });
      faro?.api.pushEvent('sign_up', { method: 'email' });
      trackAmplitudeEvent('signup', { method: 'email' });
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Don't silently discard un-synced offline writes — logout wipes the whole
      // replica incl. the op-queue (pending AND parked conflict/error ops), so
      // confirm first if anything un-synced exists. The count must match what the
      // wipe destroys, hence unsyncedOpCount() (whole queue), not pending-only.
      if (OFFLINE_ENABLED) {
        let unsynced = 0;
        try {
          unsynced = await unsyncedOpCount();
        } catch (e) {
          // A read-only courtesy check must never trap the user authenticated on a
          // shared device (the worse PHI outcome). Fail toward allowing sign-out —
          // clearReplica() wipes any surviving queue anyway.
          console.warn('Offline unsynced-op count on logout failed; proceeding:', e);
        }
        if (
          unsynced > 0 &&
          !window.confirm(
            `You have ${unsynced} unsynced change${unsynced === 1 ? '' : 's'} that will be discarded if you log out before reconnecting. Log out anyway?`,
          )
        ) {
          return; // user cancelled to keep their unsynced work
        }
      }
      if (user) {
        logEvent(analytics, 'logout', {
          method: 'Email and Password',
          userId: user.uid
        });
        faro?.api.pushEvent('logout');
        faro?.api.resetUser();
        trackAmplitudeEvent('logout');
        resetAmplitudeUser();
      }
      // The in-memory /users/me permission memo (P2) is written on EVERY session,
      // flag or no flag — so it is dropped on every logout. (The A5 claims cache
      // below is offline-only, which is why the two are not cleared together.)
      resetMePermissions();
      // Wipe the local offline replica so this user's PHI never outlives their
      // session on a shared device. Best-effort — never block logout on it.
      if (OFFLINE_ENABLED) {
        try {
          await clearReplica();
        } catch (e) {
          // Cache wipe must not block sign-out, but surface it — a surviving
          // replica is a PHI-retention risk worth seeing in logs.
          console.warn('Offline replica clear on logout failed:', e);
        }
        // Authz metadata (clinicId/role/permissions) must not outlive the
        // session either — drop the offline claims cache (A5). The /users/me
        // permission memo is dropped above, unconditionally.
        clearClaimsCache();
        // Classify the resulting onAuthStateChanged(null) as an INTENTIONAL
        // logout so AuthProvider ends the session instead of entering the
        // offline grace window. Set immediately before signOut, after the
        // confirm gate (so a cancelled logout leaves no stale flag). (A5)
        if (user) markIntentionalSignOut(user.uid, Date.now());
      }
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      // Self-heal: if signOut threw, the intent flag never got consumed by a
      // null event — clear it so it can't force-end an unrelated later session.
      if (OFFLINE_ENABLED) clearIntentionalSignOut();
      throw error;
    }
  };

  /**
   * Sign out without asking and without wiping: AuthProvider's session end keeps unsynced offline
   * changes for their author. For the workstation lock (out of PIN tries, or its Log out), where
   * whoever is at the keyboard may not be the person signed in. Login shows `notice`, if given.
   */
  const signOutKeepingChanges = async (notice?: SignOutNoticeCode) => {
    if (notice) setSignOutNotice(notice);
    // Read as a logout, so no other tab parks this session in the offline grace window.
    if (OFFLINE_ENABLED && user) markIntentionalSignOut(user.uid, Date.now());
    await signOut(auth);
  };

  const getToken = async () => {
    try {
      if (!user) return null;
      return await getIdToken(user);
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  };

  const requestPasswordReset = async (email: string) => {
    try {
      const response = await api.post('/public/forgot-password', { email });
      return response.data;
    } catch (error) {
      console.error('Error requesting password reset:', error);
      throw error;
    }
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading: loading,
    /** A5: true while running on a cached session in an offline grace window. */
    graceMode,
    login,
    signup,
    logout,
    signOutKeepingChanges,
    getToken,
    requestPasswordReset,
  };
}; 